import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { UserRole } from '../types';
import { 
    HomeIcon, 
    ClipboardDocumentListIcon, 
    ArchiveBoxIcon, 
    CurrencyDollarIcon, 
    UserIcon,
    ChartPieIcon,
    CubeIcon,
    QrCodeIcon,
    BuildingStorefrontIcon,
    ExclamationTriangleIcon,
    UserGroupIcon,
    BuildingOfficeIcon,
    DocumentTextIcon,
    ClockIcon,
    Squares2X2Icon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import {
    HomeIcon as HomeIconSolid,
    ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
    ArchiveBoxIcon as ArchiveBoxIconSolid,
    CurrencyDollarIcon as CurrencyDollarIconSolid,
    UserIcon as UserIconSolid,
    ChartPieIcon as ChartPieIconSolid,
    CubeIcon as CubeIconSolid,
    QrCodeIcon as QrCodeIconSolid,
    BuildingStorefrontIcon as BuildingStorefrontIconSolid,
    ExclamationTriangleIcon as ExclamationTriangleIconSolid,
    UserGroupIcon as UserGroupIconSolid,
    BuildingOfficeIcon as BuildingOfficeIconSolid,
    DocumentTextIcon as DocumentTextIconSolid,
    ClockIcon as ClockIconSolid,
    Squares2X2Icon as Squares2X2IconSolid
} from '@heroicons/react/24/solid';

interface NavItem {
    path: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    iconSolid: React.ComponentType<{ className?: string }>;
}

const BottomNavigation: React.FC = () => {
    const { user } = useAuth();
    const { t, dir } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const [showSuperAdminMenu, setShowSuperAdminMenu] = useState(false);

    if (!user) return null;

    // Normal user navigation
    if (user.role === UserRole.Normal) {
        const userNavItems: NavItem[] = [
            { 
                path: '/', 
                label: t('dashboard'), 
                icon: HomeIcon, 
                iconSolid: HomeIconSolid 
            },
            { 
                path: '/requests', 
                label: t('requests'), 
                icon: ClipboardDocumentListIcon, 
                iconSolid: ClipboardDocumentListIconSolid 
            },
            { 
                path: '/packages', 
                label: t('packages'), 
                icon: ArchiveBoxIcon, 
                iconSolid: ArchiveBoxIconSolid 
            },
            { 
                path: '/balance', 
                label: t('balance'), 
                icon: CurrencyDollarIcon, 
                iconSolid: CurrencyDollarIconSolid 
            },
            { 
                path: '/profile', 
                label: t('profile'), 
                icon: UserIcon, 
                iconSolid: UserIconSolid 
            },
        ];

        return (
            <nav 
                className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 shadow-lg lg:hidden"
                style={{ 
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                    paddingTop: '0.5rem'
                }}
                dir={dir}
            >
                <div className="container mx-auto px-2">
                    <div className="flex items-center justify-around">
                        {userNavItems.map((item) => {
                            const isActive = location.pathname === item.path || 
                                (item.path === '/' && location.pathname === '/');
                            const IconComponent = isActive ? item.iconSolid : item.icon;
                            
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={({ isActive }) =>
                                        `flex flex-col items-center justify-center gap-1 py-2 px-1 flex-1 max-w-[20%] rounded-lg transition-colors ${
                                            isActive
                                                ? 'text-primary'
                                                : 'text-text-secondary hover:text-text-primary'
                                        }`
                                    }
                                >
                                    <IconComponent className="h-6 w-6 flex-shrink-0" />
                                    <span className="text-[10px] font-medium text-center leading-tight line-clamp-2">{item.label}</span>
                                </NavLink>
                            );
                        })}
                    </div>
                </div>
            </nav>
        );
    }

    // Get all SuperAdmin navigation items
    const getAllSuperAdminNavItems = (): NavItem[] => {
        return [
            { 
                path: '/', 
                label: t('dashboard'), 
                icon: ChartPieIcon, 
                iconSolid: ChartPieIconSolid 
            },
            { 
                path: '/posts', 
                label: t('managePosts'), 
                icon: CubeIcon, 
                iconSolid: CubeIconSolid 
            },
            { 
                path: '/sellers', 
                label: t('manageSellers'), 
                icon: BuildingStorefrontIcon, 
                iconSolid: BuildingStorefrontIconSolid 
            },
            { 
                path: '/requests', 
                label: t('userRequests'), 
                icon: ClipboardDocumentListIcon, 
                iconSolid: ClipboardDocumentListIconSolid 
            },
            { 
                path: '/packages', 
                label: t('packages'), 
                icon: ArchiveBoxIcon, 
                iconSolid: ArchiveBoxIconSolid 
            },
            { 
                path: '/lost-packages', 
                label: t('lostPackages'), 
                icon: ExclamationTriangleIcon, 
                iconSolid: ExclamationTriangleIconSolid 
            },
            { 
                path: '/generate-qr', 
                label: t('generateQR'), 
                icon: QrCodeIcon, 
                iconSolid: QrCodeIconSolid 
            },
            { 
                path: '/scan-qr', 
                label: t('scanQR'), 
                icon: QrCodeIcon, 
                iconSolid: QrCodeIconSolid 
            },
            { 
                path: '/users', 
                label: t('manageUsers'), 
                icon: UserGroupIcon, 
                iconSolid: UserGroupIconSolid 
            },
            { 
                path: '/offices', 
                label: t('manageOffices') || 'Manage Offices', 
                icon: BuildingOfficeIcon, 
                iconSolid: BuildingOfficeIconSolid 
            },
            { 
                path: '/balances', 
                label: t('manageBalances'), 
                icon: CurrencyDollarIcon, 
                iconSolid: CurrencyDollarIconSolid 
            },
            { 
                path: '/all-transactions', 
                label: t('allTransactions'), 
                icon: DocumentTextIcon, 
                iconSolid: DocumentTextIconSolid 
            },
            { 
                path: '/activity-log', 
                label: t('activityLog'), 
                icon: ClockIcon, 
                iconSolid: ClockIconSolid 
            },
            { 
                path: '/user-approvals', 
                label: t('userApprovals') || 'Kullanıcı Onayları', 
                icon: UserGroupIcon, 
                iconSolid: UserGroupIconSolid 
            },
        ];
    };

    // Admin navigation (showing most important items)
    const getAdminNavItems = (): NavItem[] => {
        const baseItems: NavItem[] = [
            { 
                path: '/', 
                label: t('dashboard'), 
                icon: ChartPieIcon, 
                iconSolid: ChartPieIconSolid 
            },
            { 
                path: '/posts', 
                label: t('managePosts'), 
                icon: CubeIcon, 
                iconSolid: CubeIconSolid 
            },
            { 
                path: '/requests', 
                label: t('userRequests'), 
                icon: ClipboardDocumentListIcon, 
                iconSolid: ClipboardDocumentListIconSolid 
            },
            { 
                path: '/packages', 
                label: t('packages'), 
                icon: ArchiveBoxIcon, 
                iconSolid: ArchiveBoxIconSolid 
            },
        ];

        // Add role-specific items
        if (user.role === UserRole.SuperAdmin) {
            return [
                ...baseItems,
                { 
                    path: '/more', 
                    label: t('more') || 'Daha Fazla', 
                    icon: Squares2X2Icon, 
                    iconSolid: Squares2X2IconSolid 
                },
            ];
        } else if (user.role === UserRole.Office) {
            // Office role: Show only 5 main items in bottom menu
            return [
                { 
                    path: '/', 
                    label: t('dashboard'), 
                    icon: ChartPieIcon, 
                    iconSolid: ChartPieIconSolid 
                },
                { 
                    path: '/posts', 
                    label: t('managePosts'), 
                    icon: CubeIcon, 
                    iconSolid: CubeIconSolid 
                },
                { 
                    path: '/sellers', 
                    label: t('manageSellers'), 
                    icon: BuildingStorefrontIcon, 
                    iconSolid: BuildingStorefrontIconSolid 
                },
                { 
                    path: '/requests', 
                    label: t('userRequests'), 
                    icon: ClipboardDocumentListIcon, 
                    iconSolid: ClipboardDocumentListIconSolid 
                },
                { 
                    path: '/packages', 
                    label: t('packages'), 
                    icon: ArchiveBoxIcon, 
                    iconSolid: ArchiveBoxIconSolid 
                },
            ];
        } else {
            // AdminA, AdminB, Worker
            return [
                ...baseItems,
                { 
                    path: '/scan-qr', 
                    label: t('scanQR'), 
                    icon: QrCodeIcon, 
                    iconSolid: QrCodeIconSolid 
                },
            ];
        }
    };

    const adminNavItems = getAdminNavItems();
    const allSuperAdminItems = user.role === UserRole.SuperAdmin ? getAllSuperAdminNavItems() : [];

    // Check if current path is in the "More" menu (not in main bottom nav)
    const isCurrentPathInMoreMenu = user.role === UserRole.SuperAdmin && 
        allSuperAdminItems.some(item => {
            const isInMainNav = adminNavItems.some(mainItem => mainItem.path === item.path);
            return !isInMainNav && (location.pathname === item.path || (item.path === '/' && location.pathname === '/'));
        });

    const handleMoreClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setShowSuperAdminMenu(true);
    };

    const handleNavItemClick = (path: string) => {
        setShowSuperAdminMenu(false);
        navigate(path);
    };

    return (
        <>
            <nav 
                className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 shadow-lg lg:hidden"
                style={{ 
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                    paddingTop: '0.5rem'
                }}
                dir={dir}
            >
                <div className="container mx-auto px-2">
                    <div className="flex items-center justify-around">
                        {adminNavItems.map((item) => {
                            const isActive = location.pathname === item.path || 
                                (item.path === '/' && location.pathname === '/');
                            const IconComponent = isActive ? item.iconSolid : item.icon;
                            
                            // Handle "More" button for SuperAdmin
                            if (item.path === '/more' && user.role === UserRole.SuperAdmin) {
                                const isMoreActive = showSuperAdminMenu || isCurrentPathInMoreMenu;
                                const MoreIconComponent = isMoreActive ? item.iconSolid : item.icon;
                                return (
                                    <button
                                        key={item.path}
                                        onClick={handleMoreClick}
                                        className={`flex flex-col items-center justify-center gap-1 py-2 px-1 flex-1 max-w-[20%] rounded-lg transition-colors ${
                                            isMoreActive
                                                ? 'text-primary'
                                                : 'text-text-secondary hover:text-text-primary'
                                        }`}
                                    >
                                        <MoreIconComponent className="h-6 w-6 flex-shrink-0" />
                                        <span className="text-[10px] font-medium text-center leading-tight line-clamp-2">{item.label}</span>
                                    </button>
                                );
                            }
                            
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={({ isActive }) =>
                                        `flex flex-col items-center justify-center gap-1 py-2 px-1 flex-1 max-w-[20%] rounded-lg transition-colors ${
                                            isActive
                                                ? 'text-primary'
                                                : 'text-text-secondary hover:text-text-primary'
                                        }`
                                    }
                                >
                                    <IconComponent className="h-6 w-6 flex-shrink-0" />
                                    <span className="text-[10px] font-medium text-center leading-tight line-clamp-2">{item.label}</span>
                                </NavLink>
                            );
                        })}
                    </div>
                </div>
            </nav>

            {/* SuperAdmin Menu Drawer */}
            {user.role === UserRole.SuperAdmin && (
                <>
                    {/* Backdrop */}
                    {showSuperAdminMenu && (
                        <div 
                            className="fixed inset-0 bg-black/50 z-[60] lg:hidden"
                            onClick={() => setShowSuperAdminMenu(false)}
                        />
                    )}
                    
                    {/* Drawer */}
                    <div 
                        className={`fixed bottom-0 left-0 right-0 bg-card border-t border-border z-[70] shadow-2xl lg:hidden transition-transform duration-300 ease-out ${
                            showSuperAdminMenu ? 'translate-y-0' : 'translate-y-full'
                        }`}
                        style={{ 
                            paddingBottom: 'calc(var(--bottom-nav-height, 5.5rem) + env(safe-area-inset-bottom, 0px))',
                            maxHeight: '70vh',
                            overflowY: 'auto'
                        }}
                        dir={dir}
                    >
                        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
                            <h2 className="text-lg font-bold text-text-primary">{t('navigation') || 'Navigation'}</h2>
                            <button
                                onClick={() => setShowSuperAdminMenu(false)}
                                className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 text-text-secondary hover:text-text-primary transition-colors"
                                aria-label="Close menu"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                        <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {allSuperAdminItems.map((item) => {
                                const isActive = location.pathname === item.path || 
                                    (item.path === '/' && location.pathname === '/');
                                const IconComponent = isActive ? item.iconSolid : item.icon;
                                
                                return (
                                    <button
                                        key={item.path}
                                        onClick={() => handleNavItemClick(item.path)}
                                        className={`flex flex-col items-center justify-center gap-2 p-4 rounded-lg border transition-colors ${
                                            isActive
                                                ? 'bg-primary/10 border-primary text-primary'
                                                : 'bg-transparent border-border text-text-secondary hover:bg-black/5 dark:hover:bg-white/5 hover:text-text-primary hover:border-primary/50'
                                        }`}
                                    >
                                        <IconComponent className="h-7 w-7 flex-shrink-0" />
                                        <span className="text-xs font-medium text-center leading-tight">{item.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default BottomNavigation;

